import { ReactNode, Suspense, useRef, useState } from 'react';

import { useRecoilState } from 'recoil';

import { showingDockedCopilotState } from 'app/AppState';
import { useDialog } from 'components/dialog';
import { LoadingSpinner } from 'components/loading-spinner';
import { SideLayout, SideLayoutContent } from 'components/side-layout';
import { usePreferenceState } from 'data/preferences';
import { useHeaderlessMode } from 'features/application/ApplicationState';
import { styled } from 'stitches';

import { ContextualNavContainer } from './ContextualNavContainer';
import { CopilotDock } from './CopilotDock';
import { GlobalLayoutContextProvider } from './GlobalLayoutState';
import { GlobalNav } from './GlobalNav';
import { Notifications } from './notifications';
import { CopilotViewModePreference, PreferencesDialog } from './PreferencesDialog';
import { QuickNavigation } from './QuickNavigation';

const GlobalLayoutContainer = styled('div', {
  display: 'flex',
  overflow: 'hidden',
  height: 'calc(100vh - 0px)',

  variants: {
    orientation: {
      vertical: { flexDirection: 'row' },
      horizontal: { flexDirection: 'column' },
    },
  },
});

const MainLayout = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
  alignItems: 'stretch',
  position: 'relative',
  overflow: 'hidden',
  minHeight: 0,
});

export type GlobalLayoutProps = {
  children?: ReactNode;
};

export function GlobalLayout(props: GlobalLayoutProps) {
  const contextualNavRef = useRef<HTMLDivElement>(null);

  const headerless = useHeaderlessMode();

  const [selectedDrawer, setSelectedDrawer] = useState<string | null>(null);
  const [navOrientation] = usePreferenceState<'vertical' | 'horizontal'>({
    key: 'navbarOrientation',
    defaultValue: 'vertical',
  });

  const [copilotViewMode] = usePreferenceState<CopilotViewModePreference>({
    key: 'copilotViewMode',
    defaultValue: 'dock',
  });
  const [showDockedCopilot] = useRecoilState(showingDockedCopilotState);
  const showCopilot = showDockedCopilot && copilotViewMode === 'dock';

  const preferencesDialog = useDialog();

  return (
    <GlobalLayoutContextProvider
      value={{
        contextualNavRef,
      }}
    >
      <GlobalLayoutContainer orientation={navOrientation}>
        {!headerless && (
          <GlobalNav
            orientation={navOrientation}
            onDrawerSelect={setSelectedDrawer}
            onPreferences={preferencesDialog.open}
          />
        )}

        <MainLayout>
          <SideLayout>
            <ContextualNavContainer />

            <SideLayoutContent>
              <Suspense fallback={<LoadingSpinner />}>{props.children}</Suspense>
            </SideLayoutContent>
          </SideLayout>

          <CopilotDock show={showCopilot} />
        </MainLayout>
      </GlobalLayoutContainer>

      <QuickNavigation
        isOpen={selectedDrawer === 'quickNavigation'}
        onClose={() => setSelectedDrawer(null)}
      />

      <Notifications
        isOpen={selectedDrawer === 'notifications'}
        onClose={() => setSelectedDrawer(null)}
      />

      <PreferencesDialog {...preferencesDialog.getDialogProps()} />
    </GlobalLayoutContextProvider>
  );
}
